<script type="text/jsx">
import _ from 'lodash';
import { mapActions, mapMutations, mapGetters } from 'vuex';
import { toThousands } from '@/utils/money';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { autoAddTypes, planTypes, trackingPlanTypes } from '@/common/constants';
import { noopPromise } from '@/utils';
import SaveCount from '@/components/save-count';
import ConfigCycle from '@/components/service/config-billing-cycle/index';
import { isAutoAddable } from '@/plans/ZOOM_MEETING/common/utils';
import { showSyncAddonMessage } from './mx/autoAdd';

export const PLAN_UNIT_MAP = {
  license: 'license',
  user: 'user',
};

export default {
  components: { ConfigCycle, SaveCount, InputNumberWithPopover },
  props: {
    planType: String,
    // show alert warning or tooltips
    // true -> alert warning
    // false -> tooltips
    showCycleAlert: Boolean,
    showMonthlyPriceFromCycle: Boolean,
    // weather or not show capacity dropdown list
    showSelectCapacityDropdown: {
      type: Boolean,
      default: true,
    },
    capacityTips: String,
    licenseTips: String,
    selectCapacitySize: {
      type: String,
      default: 'large',
    },
    capacityUnitI18nKey: {
      type: String,
      default: 'billing.participants',
    },
    // plan unit (license/user/line)
    planUnit: {
      type: String,
      default: PLAN_UNIT_MAP.license,
      validator(value) {
        return Object.keys(PLAN_UNIT_MAP).includes(value);
      },
    },
    // current show capacity
    selectCapacity: {
      type: Number,
      required: true,
    },
    // capacity dropdown list
    capacityList: {
      type: Array,
      required: true,
      default: () => [],
    },
    // billing cycle plan list
    billingCycleList: {
      type: Array,
      required: true,
      default: () => [],
    },
    // capacity delete cta
    deleteVisible: {
      type: Boolean,
      default: true,
    },
    dividerVisible: {
      type: Boolean,
      default: true,
    },
    maxHosts: {
      type: Number,
      default: 999,
    },
    minHosts: {
      type: Number,
      default: 1,
    },
    decreaseTips: String,
    licenseAlert: String,
    beforeSwitchCycle: {
      type: Function,
      default: noopPromise,
    },
    getIncreaseTips: {
      type: Function,
      default: () => {},
    },
    unit: {
      type: String,
    },
    selectLabel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      license: 1,
      billingCycle: '',
    };
  },
  computed: {
    ...mapGetters([
      'syncBaseToSelectedPlanLicense',
      'getCurrentMeetingPlans',
      'getCurrentBasePlanInfo',
      'getSelectedPlans'
    ]),
    currentPlanList() {
      return _.map(this.billingCycleList, (p) => p.plan);
    },
    cartSelectedPlan() {
      return _.find(this.currentPlanList, (p) => p?.cartInfo?.selected_number > 0);
    },
    trackingPlanKey() {
      const planName = _.get(trackingPlanTypes, _.get(planTypes, this.planType));
      return `${planName} ${this.selectCapacity ?? this.$t(this.capacityUnitI18nKey)} `;
    },
    selectedPlanCode() {
      return _.find(this.currentPlanList, (p) => p.billing_cycle === this.billingCycle)?.plan_code;
    },
    inputUnit() {
      return this.unit || this.$t(`billing.planform.unit.${this.planUnit}`);
    },
    currentBasePlan() {
      const basePlans = this.getCurrentMeetingPlans.length ? this.getCurrentMeetingPlans : this.getCurrentBasePlanInfo?.plans;
      return _.get(basePlans, ['0'], {});
    },
  },
  methods: {
    ...mapActions(['switchCapacityAction']),
    ...mapMutations(['updateSelectPlan']),
    async switchBillingCycle(oldPlan, newPlan) {
      // when the current license less than max, get current license, otherwise get maxHosts
      const quantity = this.license <= this.maxHosts ? this.license : this.maxHosts;
      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };

      const preCartInfo = await this.beforeSwitchCycle(oldPlan, newPlanInfo);

      await this.switchCapacityAction([
        oldPlan?.plan_code,
        { ...newPlanInfo, cartInfo: { ...newPlanInfo.cartInfo, ...preCartInfo} },
      ]);

      // event tracking
      const billingCycle = this.cartSelectedPlan?.billing_cycle;
      if (this.$context?.is.zs2ze) {
        billingCycle && this.$tracking.buyInteraction(`zoom events plan`, billingCycle);
      } else {
        billingCycle && this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey} billing cycle`,
          this.$t(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
        );
      }
    },
    updateCart(newLicense, oldLicense) {
      const planCode = this.selectedPlanCode;

      if (planCode) {
        this.updateSelectPlan({
          plan_code: planCode,
          cartInfo: { selected_number: newLicense },
        });
      }

      // event tracking
      if (this.$context?.is.zs2ze) {
        planCode && this.$tracking.buyInteraction(`zoom events plan license`,
          `${this.billingCycle} - ${this.selectCapacity} attendees - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
      } else {
        planCode && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} license`,
          `${this.trackingPlanKey} - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
      }
    },
    changeLicense(newLicense) {
      const oldLicense = this.license;
      this.license = newLicense;
      const payload = [newLicense, oldLicense];

      if (this.updateShoppingCart) {
        this.updateShoppingCart(...payload)
      } else {
        this.updateCart(...payload);
      }
    },
    onSwitchCapacity(capacity) {
      const isFromTabKeyboard = document.activeElement.tagName === 'DL' && document.activeElement.getAttribute('role') === 'listbox';
      this.$emit('switch-capacity', this.selectCapacity, capacity, this.license, isFromTabKeyboard);

      if (this.$context.is.zs2ze) {
        this.$tracking.buyInteraction(`zoom events attendees`, `${this.billingCycle} - ${capacity} attendees/year`);
      } else {
        this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} attendees`, capacity);
      }
    },
    onDelete() {
      this.deleteVisible && this.$emit('delete-capacity', this.selectedPlanCode);
      this.$tracking.buyInteraction(`${this.trackingPlanKey} options`, 'remove');
    },
    focusSelect() {
      this.$refs.attendeeSelect?.focus();
    },
    toggleLicenseTips(tips) {
      tips && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} options`, 'licenses - tooltip');
    },
    changeBillingCycle(cycle) {
      this.billingCycle = cycle;
      this.$emit("switch-cycle", cycle, this.selectCapacity);
    },
    toggleCapacityTips(tips) {
      if (this.$context?.is.zs2ze) {
        tips && this.$tracking.buyInteraction(`zoom events options`, 'select number of attendees - tooltip');
      } else {
        tips && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} options`, 'licenses - tooltip');
      }
    },
    mx_renderCapacityDropdown() {
      const unitName = this.$t(this.capacityUnitI18nKey);

      return <div class="opc-capacity-config-row__capacity--wrap">
        <zm-select
          data-testid="checkout-select-8"
          ref="attendeeSelect"
          size={this.selectCapacitySize}
          value={this.selectCapacity}
          label={this.selectLabel || this.trackingPlanKey}
          onInput={this.onSwitchCapacity}
          class="opc-capacity-config-row__capacity--select">
          {this.capacityList.map((capacity) => {
            return <zm-option
              data-testid="checkout-option-9"
              key={capacity}
              value={capacity}
              label={toThousands(capacity)}
            />;
          })}
        </zm-select>
        <span class="opc-capacity-config-row__capacity--unit">{unitName}</span>
      </div>;
    },
    mx_renderCapacityView() {
      const unitName = this.$t(this.capacityUnitI18nKey);
      return <div class="opc-capacity-config-row__cycle__title">
        {this.selectCapacity} <span class='opc-capacity-config-row__capacity--unit'>{unitName}</span>
      </div>;
    },
    mx_renderCapacity() {
      return <div class="opc-capacity-config-row__capacity">
        {this.showSelectCapacityDropdown ?
          this.mx_renderCapacityDropdown() :
          this.mx_renderCapacityView()
        }
        {(this.$slots.capacityTips || !this.capacityTips) ?
          this.$slots.capacityTips :
          <zm-tooltip
            class="opc-capacity-config-row__tooltips"
            role="button"
            aria-label="Learn more about Select the number of licenses"
            content={this.capacityTips}
            placement="bottom"
            onInput={this.toggleCapacityTips}>
            <i class="zm-icon-info-outline" />
          </zm-tooltip>}
      </div>;
    },
    mx_renderLicense() {
      return <div class="opc-capacity-config-row__input__number">
        <input-number-with-popover
          value={this.license}
          onChange={this.changeLicense}
          size="large"
          controls-position="both"
          min={this.minHosts}
          max={this.maxHosts}
          increaseTips={this.getIncreaseTips(this.selectCapacity, this.license)}
          decreaseTips={this.decreaseTips}
          unit={this.inputUnit}
          label={`${this.trackingPlanKey} + ' Select the number of ${this.planUnit}'`}
        />
        {/* <span
          class="opc-capacity-config-row__input__number--unit">{this.$t(`billing.planform.unit.${this.planUnit}`)}</span> */}
        {(this.$slots.licenseTips || !this.licenseTips) ?
          this.$slots.licenseTips :
          <zm-tooltip
            class="opc-capacity-config-row__tooltips"
            role="button"
            aria-label="Learn more about Select the number of licenses"
            content={this.licenseTips}
            placement="bottom"
            onInput={this.toggleLicenseTips}>
            <i class="zm-icon-info-outline" />
          </zm-tooltip>}
      </div>;
    },
    mx_renderRemoveBtn() {
      return <zm-button
        data-testid="checkout-button-10"
        type="link"
        ghost
        size="mini"
        onClick={this.onDelete}
        aria-label={`remove ${this.trackingPlanKey}`}
        class={{ 'opc-capacity-config-row__delete-btn': true, 'opc-capacity-config-row__delete--btn': !this.deleteVisible }}
      >
        <i class="opc-capacity-config-row__delete--icon zm-icon-close" />
      </zm-button>;
    },
    mx_renderAlert(slot, title) {
      if (slot) return slot;
      if (title) return <zm-alert
        class="opc-common-info-alert"
        type="warning"
        closable={false}
        show-icon>
        <p domPropsInnerHTML={title} />
      </zm-alert>;
    },
    mx_renderDivider() {
      return this.dividerVisible ? <hr /> : null;
    },
    mx_renderCycleRow() {
      return <config-cycle
        showCycleAlert={this.showCycleAlert}
        showMonthlyPriceFromCycle={this.showMonthlyPriceFromCycle}
        billingCycle={this.billingCycle}
        billingCycleList={this.billingCycleList}
        onSwitchCycleAction={this.switchBillingCycle}
        onSyncCycle={this.changeBillingCycle}
      />
    },
  },
  render() {
    return <div class="opc-capacity-config-row">
      <div class="opc-capacity-config-row__license">
        {this.mx_renderCapacity()}
        {this.mx_renderLicense()}
        {this.mx_renderRemoveBtn()}
      </div>
      {this.mx_renderAlert(this.$slots.licenseAlert, this.licenseAlert)}
      {this.mx_renderCycleRow()}
      {this.mx_renderDivider()}
    </div>;
  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan, oldPlan) {
        if (
          plan?.cartInfo?.selected_number === oldPlan?.cartInfo?.selected_number &&
          plan?.billing_cycle === oldPlan?.billing_cycle
        ) {
          return;
        }
        // sync shopping cart form state to local form state
        this.billingCycle = plan?.billing_cycle ?? '';
        this.license = plan?.cartInfo?.selected_number || 1;
      },
    },
    license: {
      immediate: true,
      handler(newLicense, oldLicense) {
        this.$emit(
          'change-license',
          newLicense,
          oldLicense,
          { capacity: this.selectCapacity, billingCycle: this.billingCycle },
        );
      },
    },
    maxHosts: {
      immediate: true,
      handler(nv) {
        const oldLicense = this.license;
        if (this.license > nv) {
          this.changeLicense(nv);
          // when auto add enable, decrease of base plan license is no longer limited
          // current plan license will change according to the maxHosts(base plan license)
          // show notification
          const planNameKey = this.currentPlanList.find((plan) => plan.type === this.planType)?.planNameKey
          showSyncAddonMessage(planNameKey, this.currentBasePlan?.planNameKey)
        }
        this.$emit(
          'change-license',
          this.license,
          oldLicense,
          { capacity: this.selectCapacity, billingCycle: this.billingCycle },
        );
      },
    },
    // Auto add
    // @jira https://zoomvideo.atlassian.net/browse/OBT-2083
    // watch base plan license change to trigger current plan license change
    syncBaseToSelectedPlanLicense: {
      immediate: false,
      handler(nv) {
        if(!nv) return
        if(!(isAutoAddable() && autoAddTypes.includes(this.planType))) {
          return
        }
        const { baseLicense } = nv
        if(baseLicense < this.license){
          this.changeLicense(baseLicense)
          const planNameKey = this.currentPlanList.find((plan) => plan.type === this.planType)?.planNameKey
          showSyncAddonMessage(planNameKey, this.currentBasePlan?.planNameKey)
        }
      },
    }
  },
  created() {
    // A closure (timeId) needs to be generated for each config row instance
    this.updateShoppingCart = _.debounce(this.updateCart, 200);
  }
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/_variables.scss';
@import '@/styles/scss-utils.scss';

.opc-capacity-config-row {
  &__tooltips {
    color: var(--zoom-color-blue-default, #0B5CFF);
    font-size: 14px;
    margin-left: 4px;
    cursor: pointer;
  }

  &__license {
    display: flex;
    justify-content: space-between;
    @include media(mobile, tablet) {
      position: relative;
      flex-wrap: wrap;
    }
  }

  &__input__number {
    @include media(mobile, tablet) {
      margin-top: 12px;
      width: 100%;
    }
    &--unit {
      margin-left: 8px;
    }
  }

  $capacityHeight: 40px;

  &__delete-btn.zm-button {
    @include media(mobile, tablet) {
      position: absolute;
      top: $capacityHeight / 2;
      right: 0;
      transform: translateY(-50%);
    }
  }

  &__delete {
    &--btn {
      visibility: hidden;
    }

    &--icon {
      color: #6E7680;
      font-size: 16px;
    }
  }

  &__capacity {
    display: flex;
    align-items: center;
    @include media(mobile) {
      padding-right: $capacityHeight;
      width: 100%;
    }

    &--select {
      width: 120px;
      height: $capacityHeight;

      .zm-select-input {
        .zm-select-span {
          &__inner {
            font-weight: 700;
            font-size: 18px;
            color: var(--zoom-color-blue-default, #0B5CFF);
          }
        }
      }
    }

    &--unit {
      margin-left: 8px;
      text-transform: capitalize;
    }
  }

  &__cycle {
    margin-top: 16px;

    &__title {
      display: flex;
      align-items: center;
      font-weight: 700;
      font-size: 18px;
      line-height: 150%;
      color: #00053D;
      height: 100%;
    }

    &__plan {
      margin-top: 8px;

      &__2.opc-card-radio {
        gap: 16px;
      }

      &__3.opc-card-radio {
        gap: 12px;
      }

      .zm-radio {
        flex: 1;
        min-width: 0;

        &__wrap {
          width: 100%;
        }

        &__label {
          width: 100%;

          &--inner {
            width: 100%;
            /* padding-right: 2px !important; */
          }

          &.is-disabled {
            .opc-capacity-config-row__cycle__plan__price--amount,
            .opc-capacity-config-row__cycle__plan__price--unit {
              color: #9191A4;
            }
          }
        }
      }

      &__title {
        font-weight: 700;
        font-size: 16px;
        text-transform: capitalize;
        display: flex;
        align-items: center;
        flex-wrap: wrap; // for i18n
      }

      &__price {
        display: flex;
        flex-wrap: wrap;
        align-items: center;

        &--amount, &--unit {
          color: #00053D;
          line-height: 150%;
        }

        &--amount {
          font-weight: 700;
          font-size: 22px;
          word-break: break-all;
        }

        &--unit {
          font-weight: 500;
          font-size: 12px;
          color: #515079;
          word-break: break-all;
        }
      }
    }

    &__disabled--desc {
      font-family: Almaden Sans;
      font-size: 14px;
      font-weight: 500;
      line-height: 21px;
      letter-spacing: 0em;
      text-align: left;
      color: #515079;
      margin-top: 8px;
    }

    &__plan__save {
      margin-left: 4px;
    }
  }
}

.opc-warning-message {
  display: flex;
  align-items: center;
  background: #FEF7D9;
  color: #815A30;

  &__addon {
    margin-top: 16px;
  }
}
</style>
